import React, { useEffect } from 'react';
import { fromZonedTime } from 'date-fns-tz';
import { useForm, Controller } from 'react-hook-form';
import { parse, isBefore, isAfter, addYears, addMinutes, startOfDay } from 'date-fns';
import { navigate } from 'gatsby';
import gtm from '@periodica/analytics/gtm';
import * as Select from '@periodica/ui-kit/simple-select';

import {
  TextField,
  FormField,
  PhoneField,
  Paragraph as P,
  Button,
  DateField,
  TimeField,
} from '@periodica/ui-kit';
import * as Checkbox from '@periodica/ui-kit/checkbox';
import { AgreementText, AgreementTextPromo } from '@periodica/shared-components';

import { DEFAULT_DESIGN_PRICE } from '@consts/designConsts';
import { getCertificatePrices } from '@utils/productsForPrice';
import { useProducts } from '@hooks/useProducts';
import { Switcher } from '@components/Blocks/Switcher';
import { CertificateImageUpload } from '@components/GiftPage/components/CertificateImageUpload';
import { FullWidthBlock } from '@components/Layouts/FullWidthBlock';
import { generateCustomGiftSvg } from '@utils/generateCustomGiftSvg';
import {
  DEFAULT_PRICE,
  GIFT_OPTION_DEFAULT,
  GIFT_OPTION_CUSTOM,
  GIFT_SUM_DEFAULT,
  GIFT_SUM_CUSTOM,
  CUSTOM_CERT_ID_PROD,
  CUSTOM_CERT_ID_DEV,
  GIFT_SEND_NOW,
  GIFT_SEND_CUSTOM,
  DELIVERY_WAY_SMS,
  TYPE_RECIPIENT_PERSON,
} from '../consts';

import SumSelector from './SumSelector';
import CertificatePatternField from './CertificatePatternField';
import { GiftBonusBlock } from './GiftBonusBlock';
import { getGiftCards } from '../../../periodicaApi/gift';
import { FormBlockHeading } from './FormBlockHeading';
import { GiftDeliveryStep } from './GiftDeliveryStep';
import { validatePhoneNumber } from '../validate';

import styles from './GiftForm.module.scss';

const switcherPictureOptions = [
  {
    id: GIFT_OPTION_DEFAULT,
    title: 'Все сертификаты',
  },
  {
    id: GIFT_OPTION_CUSTOM,
    title: 'Загрузить свое фото',
  },
];

const switcherSumOptions = [
  {
    id: GIFT_SUM_DEFAULT,
    title: 'Выбрать из списка',
  },
  {
    id: GIFT_SUM_CUSTOM,
    title: 'Указать свой номинал',
  },
];

const switcherTimeOptions = [
  {
    id: GIFT_SEND_NOW,
    title: 'Сейчас',
  },
  {
    id: GIFT_SEND_CUSTOM,
    title: 'Выбрать время',
  },
];

const isDevelopment = process.env.GATSBY_ACTIVE_ENV === 'development';

/* eslint-disable @typescript-eslint/naming-convention */
const timezoneOptions = {
  'Europe/Moscow': 'Московское время',
  'Europe/Kaliningrad': 'Калининградское (МСК-1)',
  'Europe/Samara': 'Самарское (МСК+1)',
  'Asia/Yekaterinburg': 'Екатеринбургское (МСК+2)',
  'Asia/Omsk': 'Омское (МСК+3)',
  'Asia/Krasnoyarsk': 'Красноярское (МСК+4)',
  'Asia/Irkutsk': 'Иркутское (МСК+5)',
  'Asia/Yakutsk': 'Якутское (МСК+6)',
  'Asia/Vladivostok': 'Владивостокское (МСК+7)',
  'Asia/Srednekolymsk': 'Среднеколымское (МСК+8)',
  'Asia/Kamchatka': 'Камчатское (МСК+9)',
};
/* eslint-enable */

// to do: типизировать и разбить на компоненты
export function GiftForm({ values = {} }) {
  const [state, setState] = React.useState({
    text: '',
    sum: Number(values?.sum?.sum) || DEFAULT_PRICE,
    giftCards: [],
    giftPrices: [],
  });

  const [giftType, setGiftType] = React.useState(GIFT_OPTION_DEFAULT);
  const [giftSumType, setGiftSumType] = React.useState(GIFT_SUM_DEFAULT);
  const [giftTimeType, setGiftTimeType] = React.useState(GIFT_SEND_NOW);
  const [savedSumObj, setSavedSumObj] = React.useState(values?.sum || null);
  const [savedCustomSum, setSavedCustomSum] = React.useState(values?.customSum || null);

  const [savedCustomValues, setSavedCustomValues] = React.useState({
    customDate: values?.customDate || '',
    customTime: values?.customTime || '',
    timezone: values?.timezone || Object.keys(timezoneOptions)[0],
  });

  const [agreePolicy, setAgreePolicy] = React.useState(false);

  const hasError = (value) => {
    if (value.isTextEnabled === false || (value && value.text?.trim())) {
      return true;
    }

    return 'Введите пожелание';
  };

  const hasCustomImageError = (value) => {
    if (giftType === GIFT_OPTION_DEFAULT || value) {
      return true;
    }

    return 'Пожалуйста, загрузите изображение';
  };

  const parseDate = (value) => parse(value, 'dd.MM.yyyy', new Date());

  const parseTime = (value, date) => parse(value, 'HH:mm', date);

  const isPhotobook = (products) => products?.find((option) => option.productType === 'book');

  const { sum } = state;

  const { allProducts } = useProducts();
  const filteredProducts = allProducts.filter(
    (product) =>
      !['gift', 'wedding', 'detskaya', 'postcard', 'wrappingPaper'].includes(product.productType) &&
      !product.isUnavailable
  );

  const giftPrices = getCertificatePrices(filteredProducts);
  const defaultProduct = giftPrices.find((option) => option.sum === sum);

  const initialValues = () => ({
    pattern: {
      image: values?.pattern?.image || state.giftCards[0]?.image,
      backImage: values?.pattern?.backImage || state.giftCards[0]?.backImage,
      frontLayout: values?.pattern?.frontLayout || state.giftCards[0]?.frontLayout,
      template_id: values?.pattern?.template_id || state.giftCards[0]?.template_id,
      slideId: values?.pattern?.slideId || state.giftCards[0]?.slideId,
      text: values?.pattern?.text || '',
      isTextEnabled: values?.pattern?.isTextEnabled || false,
    },
    sum: null,
    customSum: values?.customSum || null,
    isDesignOn: values?.isDesignOn || false,
    email: values?.email || '',
    emailTo: values?.emailTo || '',
    nameFrom: values?.nameFrom || '',
    nameTo: values?.nameTo || '',
    phoneTo: values?.phoneTo || '',
    phone: values?.phone || '',
    deliveryWay: values?.deliveryWay || DELIVERY_WAY_SMS,
    deliveryRecipient: values?.deliveryRecipient || TYPE_RECIPIENT_PERSON,
    previewUrl: values?.previewUrl || null,
    client_newsletter_agreed: values?.client_newsletter_agreed,
    customDate: values?.customDate || '',
    customTime: values?.customTime || '',
    timezone: values?.timezone || Object.keys(timezoneOptions)[0],
  });

  const form = useForm({
    defaultValues: initialValues(),
    mode: 'onSubmit',
    shouldFocusError: true,
    shouldUnregister: true,
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    trigger,
    getValues,
  } = form;

  const watchSum = watch('sum');
  const watchIsDesignOn = watch('isDesignOn');
  const totalSum = watchIsDesignOn
    ? (savedSumObj?.sum || 0) + DEFAULT_DESIGN_PRICE
    : savedSumObj?.sum;

  const getInfo = async () => {
    const giftCards = await getGiftCards();
    setState((prev) => ({
      ...prev,
      giftCards,
      giftPrices,
    }));
    return null;
  };

  const hasErrorCustomDate = (value) => {
    if (!value) return '';
    if (value.length !== 10) return 'Введите дату в формате дд.мм.гггг';

    const inputDate = startOfDay(parseDate(value));
    const today = startOfDay(new Date());
    const maxDate = addYears(today, 2);

    if (isBefore(inputDate, today)) {
      return 'Введите дату в будущем';
    }
    if (isAfter(inputDate, maxDate)) {
      return 'Максимальное время отправки — 2 года с текущей даты';
    }
    return true;
  };

  const hasErrorCustomTime = (value) => {
    if (value.length !== 5) return 'Введите время в формате чч:мм';

    const inputCustomDate = getValues('customDate');
    const timezone = getValues('timezone');

    if (!inputCustomDate || !timezone) return true;

    const date = parseDate(inputCustomDate);
    const fullDate = parseTime(value, date);

    const fullDateUTC = fromZonedTime(fullDate, timezone);

    const now = new Date();

    const delay30Min = addMinutes(new Date(), 30);

    if (now >= fullDateUTC) {
      return 'Введите время в будущем';
    }

    if (delay30Min >= fullDateUTC) {
      return 'Введите время не ранее, чем через 30 минут от текущего';
    }

    return true;
  };

  const handleGiftSumTypeChange = (type) => {
    if (type === giftSumType) {
      return;
    }
    if (type === GIFT_SUM_DEFAULT) {
      setValue('sum', savedSumObj);
    } else {
      setValue('customSum', savedCustomSum);
    }

    if (!isPhotobook(savedSumObj?.products)) {
      setValue('isDesignOn', false);
    }
    gtm.logEvent({ name: 'event_site_gift_select-gift-sum-type' });
    setGiftSumType(type);
  };

  const handleGiftTypeChange = (type) => {
    if (type === giftType) {
      return;
    }
    gtm.logEvent({ name: 'event_site_gift_select-gift-type' });
    setGiftType(type);
  };

  const handleGiftTimeTypeChange = (type) => {
    if (type === giftTimeType) return;

    const currentDate = getValues('customDate');
    const currentTime = getValues('customTime');
    const currentTimezone = getValues('timezone');

    setSavedCustomValues({
      customDate: currentDate,
      customTime: currentTime,
      timezone: currentTimezone,
    });

    if (type === GIFT_SEND_CUSTOM) {
      setValue('customDate', savedCustomValues.customDate);
      setValue('customTime', savedCustomValues.customTime);
      setValue('timezone', savedCustomValues.timezone);
    }

    gtm.logEvent({ name: 'event_site_gift_select-gift-send-type' });
    setGiftTimeType(type);
  };

  const onSubmit = async (data) => {
    let pattern;
    if (giftType === GIFT_OPTION_DEFAULT) {
      pattern = data?.pattern?.image
        ? { ...data.pattern }
        : { ...state.giftCards[0], text: data.pattern.text };
    } else {
      pattern = {
        image: data.previewUrl,
        frontLayout: generateCustomGiftSvg(data?.previewUrl || ''),
        template_id: isDevelopment ? CUSTOM_CERT_ID_DEV : CUSTOM_CERT_ID_PROD,
        slideId: state.giftCards[0]?.slideId,
        text: '',
        isTextEnabled: false,
      };
    }
    const patternText = pattern?.isTextEnabled === false ? '' : pattern.text;
    const normalizedPattern = {
      ...pattern,
      text: patternText,
    };

    gtm.logEvent({ name: 'event_site_gift_finish-order' });
    navigate('/products/gift/confirmation/', {
      state: {
        ...data,
        pattern: normalizedPattern,
        giftType,
        timezoneTitle: timezoneOptions[data.timezone],
      },
    });
  };

  useEffect(() => {
    if (giftType === GIFT_OPTION_DEFAULT && values?.previewUrl) {
      setValue('pattern', {
        image: state.giftCards[0]?.image,
        backImage: state.giftCards[0]?.backImage,
        frontLayout: state.giftCards[0]?.frontLayout,
        template_id: state.giftCards[0]?.template_id,
        slideId: state.giftCards[0]?.slideId,
        text: '',
        isTextEnabled: false,
      });
    }
    // TODO: разобраться с зависимостями
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftType]);

  useEffect(() => {
    // need to correct render of giftType and price when state exists and page reloads
    setValue('sum', defaultProduct);
    setGiftType(values?.giftType || GIFT_OPTION_DEFAULT);
    setSavedSumObj(defaultProduct);
    setGiftSumType(values?.customSum ? GIFT_SUM_CUSTOM : GIFT_SUM_DEFAULT);
    setGiftTimeType(
      values?.customDate || values?.customTime || values?.timezone
        ? GIFT_SEND_CUSTOM
        : GIFT_SEND_NOW
    );
    // TODO: разобраться с зависимостями
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    getInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} id="giftForm">
      <div className={styles.formWrapper}>
        <div className={styles.formBlock}>
          <FormBlockHeading>1. Укажите получателя</FormBlockHeading>
          <Controller
            render={({ field: { onChange, value, ref } }) => (
              <FormField
                label="Имя получателя"
                name="nameTo"
                error={errors && errors.nameTo && errors.nameTo.message}
              >
                <TextField
                  ref={ref}
                  name="nameTo"
                  onChange={(val) => onChange(val)}
                  onBlur={() => value && gtm.logEvent({ name: 'event_site_gift_enter-name-to' })}
                  value={value}
                  error={errors && errors.nameTo && errors.nameTo.message}
                />
              </FormField>
            )}
            control={control}
            name="nameTo"
            rules={{
              required: 'Введите Имя получателя',
            }}
          />
        </div>
        <div className={styles.formBlock}>
          <FormBlockHeading>2. Выберите дизайн</FormBlockHeading>
          <div className={styles.switcher}>
            <Switcher
              activeOption={giftType}
              options={switcherPictureOptions}
              onSwitch={handleGiftTypeChange}
            />
          </div>
          <P size="medium" className={styles.sliderLabel}>{`Выберите фон${
            giftType === GIFT_OPTION_DEFAULT ? ' и добавьте пожелание' : ''
          }`}</P>
        </div>
      </div>
      {giftType === GIFT_OPTION_DEFAULT ? (
        <FullWidthBlock className={styles.formSlider}>
          <Controller
            render={({ field: { onChange, value, ref } }) => (
              <CertificatePatternField
                ref={ref}
                onSlideChange={(pattern) => {
                  onChange({
                    ...value,
                    image: pattern?.image,
                    backImage: pattern?.backImage,
                    frontLayout: pattern?.frontLayout,
                    template_id: pattern?.template_id,
                    slideId: pattern?.slideId,
                    isTextEnabled: pattern?.isTextEnabled,
                  });
                }}
                onTextChange={(text) => onChange({ ...value, text })}
                value={value}
                patterns={state.giftCards}
                error={errors && errors.pattern && errors.pattern.message}
              />
            )}
            name="pattern"
            control={control}
            rules={{
              validate: (value) => hasError(value),
            }}
          />
        </FullWidthBlock>
      ) : (
        <div className={styles.formWrapper}>
          <div className={styles.customGiftBlock}>
            <div className={styles.customPhotoContainer}>
              <Controller
                render={({ field: { onChange, value } }) => (
                  <CertificateImageUpload
                    onUploadPhotos={(file) => {
                      onChange(file);
                    }}
                    onDelete={() => onChange(null)}
                    uploadedFile={value}
                    error={errors && errors.previewUrl && errors.previewUrl.message}
                  />
                )}
                name="previewUrl"
                control={control}
                rules={{
                  validate: (value) => hasCustomImageError(value),
                }}
              />
            </div>
          </div>
        </div>
      )}
      <div className={styles.formWrapper}>
        <div className={styles.formBlock}>
          <FormBlockHeading>3. Выберите номинал</FormBlockHeading>
          <div className={styles.switcher}>
            <Switcher
              activeOption={giftSumType}
              options={switcherSumOptions}
              onSwitch={handleGiftSumTypeChange}
            />
          </div>
          {giftSumType === GIFT_SUM_DEFAULT ? (
            <div className={styles.sumWrapper}>
              <Controller
                render={({ field: { onChange, value, ref } }) => (
                  <SumSelector
                    ref={ref}
                    options={state.giftPrices}
                    onChange={(val) => {
                      if (val) {
                        gtm.logEvent({ name: 'event_site_gift_select-price' });
                        setSavedSumObj(val);
                      }
                      if (!isPhotobook(val?.products)) {
                        setValue('isDesignOn', false);
                      }
                      onChange(val);
                    }}
                    value={value}
                    error={errors && errors.sum && errors.sum.message}
                  />
                )}
                name="sum"
                control={control}
                rules={{
                  required: 'Выберите номинал сертификата',
                  min: {
                    value: 100,
                    message: 'Выберите номинал сертификата',
                  },
                }}
              />
              <Controller
                control={control}
                name="isDesignOn"
                defaultValue={false}
                render={({ field: { onChange, value } }) =>
                  isPhotobook(watchSum?.products) ? (
                    <Checkbox.SingleCheckbox
                      label="Добавить услугу «Соберем фотокнигу за вас»"
                      description={`за ${DEFAULT_DESIGN_PRICE.toLocaleString('ru-RU')} ₽`}
                      onChange={(val) => onChange(val)}
                      checked={value}
                    />
                  ) : null
                }
              />
            </div>
          ) : (
            <Controller
              render={({ field: { onChange, value, ref } }) => (
                <FormField
                  label="Номинал сертификата"
                  name="customSum"
                  error={errors && errors.customSum && errors.customSum.message}
                >
                  <TextField
                    ref={ref}
                    name="customSum"
                    error={errors && errors.customSum && errors.customSum.message}
                    onChange={(val) => {
                      const newVal = Number(`${val}`.replace(/[^\d]/gi, ''));
                      setSavedCustomSum(newVal);
                      onChange(newVal);
                    }}
                    onBlur={() => value && gtm.logEvent({ name: 'event_site_gift_enter-sum' })}
                    value={value || ''}
                    type="text"
                    placeholder="от 1500 ₽"
                  />
                </FormField>
              )}
              control={control}
              name="customSum"
              rules={{
                required: 'Введите номинал сертификата',
                min: {
                  value: 1500,
                  message: 'Минимальная сумма сертификата 1500 ₽',
                },
              }}
            />
          )}
          <GiftBonusBlock sum={giftSumType === GIFT_SUM_DEFAULT ? totalSum : savedCustomSum} />
        </div>

        <div className={styles.formBlock}>
          <FormBlockHeading>4. От кого</FormBlockHeading>
          <div className={styles.formField}>
            <Controller
              render={({ field: { onChange, value, ref } }) => (
                <FormField
                  label="Имя отправителя"
                  name="nameFrom"
                  error={errors && errors.nameFrom && errors.nameFrom.message}
                >
                  <TextField
                    ref={ref}
                    name="nameFrom"
                    onBlur={() =>
                      value &&
                      !(errors && errors.nameFrom && errors.nameFrom.message) &&
                      gtm.logEvent({ name: 'event_site_gift_enter-name-from' })
                    }
                    error={errors && errors.nameFrom && errors.nameFrom.message}
                    onChange={(val) => onChange(val)}
                    value={value}
                  />
                </FormField>
              )}
              control={control}
              name="nameFrom"
              rules={{
                required: 'Введите имя отправителя',
              }}
            />
          </div>
          <div className={styles.formField}>
            <Controller
              render={({ field: { onChange, value, ref } }) => (
                <FormField
                  label="E-mail отправителя"
                  name="email"
                  description="Отправим подтверждение заказа и оплаты"
                  error={errors && errors.email && errors.email.message}
                >
                  <TextField
                    ref={ref}
                    name="email"
                    onBlur={() =>
                      value &&
                      !(errors && errors.email && errors.email.message) &&
                      gtm.logEvent({ name: 'event_site_gift_enter-mail' })
                    }
                    error={errors && errors.email && errors.email.message}
                    onChange={(val) => onChange(val)}
                    value={value}
                    type="email"
                  />
                </FormField>
              )}
              control={control}
              name="email"
              rules={{
                required: 'Введите E-mail отправителя',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Введите E-mail в формате name@domain.com',
                },
              }}
            />
          </div>
          <Controller
            render={({ field: { onChange, value, ref } }) => (
              <FormField
                name="phone"
                label="Телефон отправителя"
                error={errors && errors.phone && errors.phone.message}
              >
                <PhoneField
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  onBlur={() =>
                    value &&
                    !(errors && errors.phone && errors.phone.message) &&
                    gtm.logEvent({ name: 'event_site_gift_enter-phone' })
                  }
                  name="phone"
                  type="text"
                  placeholder="+7 (___) ___-__-__"
                  error={errors && errors.phone && errors.phone.message}
                />
              </FormField>
            )}
            name="phone"
            control={control}
            rules={{
              required: 'Введите телефон отправителя',
              validate: (value) => validatePhoneNumber(value),
            }}
          />
        </div>

        <GiftDeliveryStep
          control={control}
          errors={errors}
          setValue={setValue}
          deliveryWay={watch('deliveryWay')}
          deliveryRecipient={watch('deliveryRecipient')}
        />

        <div className={styles.formBlock}>
          <FormBlockHeading>6. Время отправки</FormBlockHeading>
          <div className={styles.switcher}>
            <Switcher
              activeOption={giftTimeType}
              options={switcherTimeOptions}
              onSwitch={handleGiftTimeTypeChange}
            />
          </div>

          {giftTimeType === GIFT_SEND_NOW && (
            <P size="large">Отправим сертификат сегодня, сразу после оплаты.</P>
          )}

          {giftTimeType === GIFT_SEND_CUSTOM && (
            <>
              <div>
                <Controller
                  control={control}
                  name="customDate"
                  rules={{
                    required: 'Введите дату в формате дд.мм.гггг',
                    validate: (value) => hasErrorCustomDate(value),
                  }}
                  render={({ field: { onChange, value, ref, onBlur } }) => (
                    <FormField
                      label="Дата"
                      name="date"
                      error={errors && errors.customDate && errors.customDate.message}
                    >
                      <DateField
                        ref={ref}
                        onChange={(val) => {
                          setSavedCustomValues((prev) => ({
                            ...prev,
                            customDate: val,
                          }));

                          onChange(val);
                        }}
                        value={value || ''}
                        error={errors && errors.customDate && errors.customDate.message}
                        type="text"
                        placeholder="ДД.ММ.ГГГГ"
                        onBlur={(e) => {
                          onBlur(e);
                          trigger('customDate');
                          const customTime = getValues('customTime');
                          if (customTime) {
                            trigger('customTime');
                          }
                          if (
                            value &&
                            !(errors && errors.customDate && errors.customDate.message)
                          ) {
                            gtm.logEvent({ name: 'event_site_gift_customDate' });
                          }
                        }}
                      />
                    </FormField>
                  )}
                />
              </div>
              <div className={styles.sendCustomTimeBlock}>
                <Controller
                  control={control}
                  name="customTime"
                  rules={{
                    required: 'Введите время в формате чч:мм',
                    validate: (value) => hasErrorCustomTime(value),
                  }}
                  render={({ field: { onChange, value, ref, onBlur } }) => (
                    <FormField
                      label="Время"
                      name="time"
                      error={errors && errors.customTime && errors.customTime.message}
                    >
                      <TimeField
                        ref={ref}
                        value={value || ''}
                        onChange={(val) => {
                          setSavedCustomValues((prev) => ({
                            ...prev,
                            customTime: val,
                          }));
                          onChange(val);
                        }}
                        type="text"
                        placeholder="__:__"
                        error={errors && errors.customTime && errors.customTime.message}
                        onBlur={(e) => {
                          onBlur(e);
                          trigger('customTime');
                          if (
                            value &&
                            !(errors && errors.customTime && errors.customTime.message)
                          ) {
                            gtm.logEvent({ name: 'event_site_gift_customTime' });
                          }
                        }}
                      />
                    </FormField>
                  )}
                />

                <Controller
                  control={control}
                  name="timezone"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <FormField label="Часовой пояс">
                      <Select.Provider
                        selectedValue={value}
                        setSelectedValue={(val) => {
                          setSavedCustomValues((prev) => ({
                            ...prev,
                            timezone: val,
                          }));
                          gtm.logEvent({ name: 'event_site_gift_timezone' });
                          onChange(val);
                        }}
                      >
                        <Select.Select
                          placeholder="Выберите часовой пояс"
                          onBlur={(e) => {
                            onBlur(e);
                            const customTime = getValues('customTime');
                            if (customTime) {
                              trigger('customTime');
                            }
                          }}
                        >
                          {timezoneOptions[value]}
                        </Select.Select>
                        <Select.Popover className={styles.popover}>
                          {Object.entries(timezoneOptions).map(([key, title]) => (
                            <Select.Option key={key} value={key}>
                              {title}
                            </Select.Option>
                          ))}
                        </Select.Popover>
                      </Select.Provider>
                    </FormField>
                  )}
                />
              </div>
            </>
          )}
        </div>

        <div className={styles.submitGroup}>
          <Button
            form="giftForm"
            size="xLarge"
            className={styles.giftButtonMargin}
            disabled={!agreePolicy}
          >
            Оформить сертификат
          </Button>
          <Controller
            control={control}
            name="client_newsletter_agreed"
            defaultValue={false}
            render={({ field: { value, onChange } }) => (
              <Checkbox.SingleCheckbox
                size="medium"
                label={<AgreementTextPromo />}
                onChange={(val) => onChange(val)}
                checked={value}
                value={value}
              />
            )}
          />
          <Checkbox.SingleCheckbox
            size="medium"
            label={<AgreementText />}
            onChange={setAgreePolicy}
            checked={agreePolicy}
          />
        </div>
      </div>
    </form>
  );
}
